// =============================================================================
// SIREPPAR - Card de alertas pendientes en el dashboard
// =============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  useAlertasPartido,
  useAlertasInternas,
  formatPlazoAlerta,
  type Alerta,
  type AlertasResponse,
  type SeveridadAlerta,
} from '../../hooks/useAlertasPartido';

const puntoPorSeveridad: Record<SeveridadAlerta, string> = {
  CRITICA: 'bg-red-500',
  ALTA: 'bg-amber-500',
  INFO: 'bg-blue-500',
};

function labelAccion(alerta: Alerta): string {
  switch (alerta.tipo) {
    case 'OBSERVACION_PENDIENTE':
    // La observación de una alianza la puede responder cualquiera de sus
    // agrupaciones, así que también es una acción para el que la ve.
    case 'ALIANZA_OBSERVACION_PENDIENTE':
      return 'Responder ahora →';
    case 'RESPUESTA_PENDIENTE_REVISION':
      return 'Revisar →';
    case 'MANDATO_POR_VENCER':
      return 'Gestionar →';
    case 'RENDICION_ANUAL_POR_VENCER':
    case 'RENDICION_CAMPANA_POR_VENCER':
      return 'Presentar →';
    // Consultas a la SEP (#391)
    case 'CONSULTA_PENDIENTE':
      return 'Responder →';
    // Consulta que le inició la SEP al partido: le toca contestar.
    case 'CONSULTA_RECIBIDA':
      return 'Responder →';
    case 'CONSULTA_RESPONDIDA':
      return 'Ver respuesta →';
    default:
      return 'Ver →';
  }
}

/**
 * Roles internos que pueden responder una consulta de un partido. Espejo de
 * Roles.INTERNOS_ESCRITURA del backend. Mesa de Entradas recibe el aviso de
 * consultas pendientes, pero no puede responder: lo ve sin el botón.
 */
const ROLES_RESPONDEN_CONSULTAS = ['ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR'];

function ItemAlerta({ alerta, rol }: { alerta: Alerta; rol?: string }) {
  const sinAccion = alerta.tipo === 'CONSULTA_PENDIENTE' && !(rol && ROLES_RESPONDEN_CONSULTAS.includes(rol));
  const dias = formatPlazoAlerta(alerta);

  return (
    <li className="px-4 py-4 sm:px-6 hover:bg-gray-50 transition">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center min-w-0 flex-1">
          <div
            className={`flex-shrink-0 h-2.5 w-2.5 rounded-full ${puntoPorSeveridad[alerta.severidad]}`}
            aria-hidden="true"
          />
          <div className="ml-4 min-w-0">
            <p className="text-md font-medium text-gray-900 truncate">{alerta.titulo}</p>
            <p className="text-sm text-gray-500 truncate">{alerta.detalle}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {dias && (
            <span
              className={`text-sm font-medium text-right max-w-[8rem] ${
                alerta.severidad === 'CRITICA' ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              {dias}
            </span>
          )}
          {!sinAccion && (
          <Link
            to={alerta.linkHref}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {labelAccion(alerta)}
          </Link>
          )}
        </div>
      </div>
    </li>
  );
}

interface Props {
  data: AlertasResponse | undefined;
  isLoading: boolean;
  extraHeaderActions?: React.ReactNode;
  /** Rol del usuario, para decidir qué acciones se muestran. */
  rol?: string;
}

function AlertasCardView({ data, isLoading, extraHeaderActions, rol }: Props) {
  if (!data) return null;

  return (
    <section id="alertas" className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          {data.total > 0
            ? `Tenés ${data.total} ${data.total === 1 ? 'alerta pendiente' : 'alertas pendientes'}`
            : 'Alertas'}
        </h3>
        <div className="flex items-center gap-3">
          {data.total > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-bold bg-red-100 text-red-700">
              {data.total}
            </span>
          )}
          {extraHeaderActions}
        </div>
      </div>

      {isLoading ? (
        <p className="px-4 py-6 text-sm text-center text-gray-400">Cargando alertas…</p>
      ) : data.alertas.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <svg
            className="mx-auto h-10 w-10 text-green-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-2 text-sm text-gray-500">No tenés alertas pendientes.</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {data.alertas.map(alerta => (
            <ItemAlerta key={alerta.id} alerta={alerta} rol={rol} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function AlertasCardPartido() {
  const { data, isLoading } = useAlertasPartido();
  return <AlertasCardView data={data} isLoading={isLoading} />;
}

export function AlertasCardInterna() {
  const { data, isLoading } = useAlertasInternas();
  const { user } = useAuth();
  const [mostrarRespuestas, setMostrarRespuestas] = useState(true);

  // Las alertas de vencimiento de expedientes se quitaron del dashboard interno:
  // el plazo de cada expediente ya se ve en la columna Días de la bandeja de
  // trabajo, que es donde se trabaja. Acá solo tapaban lo que exige una acción.
  const dataFiltrada = data
    ? (() => {
        const alertas = data.alertas.filter(a =>
          a.tipo === 'RESPUESTA_PENDIENTE_REVISION' ? mostrarRespuestas : true,
        );
        return { total: alertas.length, alertas };
      })()
    : undefined;

  const filtros = (
    <div className="flex items-center gap-3 text-sm text-gray-600">
      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={mostrarRespuestas}
          onChange={e => setMostrarRespuestas(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Respuestas pendientes
      </label>
    </div>
  );

  return (
    <AlertasCardView data={dataFiltrada} isLoading={isLoading} extraHeaderActions={filtros} rol={user?.rol} />
  );
}

// Backwards-compat: el default sigue siendo la card del partido.
export default AlertasCardPartido;
